import React from 'react';
import PropTypes from 'prop-types';
import NeoModal from './NeoModal';
import NeoButton from './NeoButton';

/**
 * NeoConfirmDialog
 * Componente declarativo React para diálogos de confirmação premium.
 * Utiliza o NeoModal e NeoButton integrando cores e botonaria oficial da Taça Outono.
 */
const NeoConfirmDialog = ({
    isOpen,
    toggle,
    title = 'Confirmar Ação',
    subtitle = '',
    message = 'Você tem certeza que deseja prosseguir com esta ação?',
    icon = 'mdi-alert-circle-outline',
    iconVariant = 'warning', // 'warning' | 'danger' | 'success' | 'info'
    confirmText = 'Sim, continuar',
    cancelText = 'Cancelar',
    onConfirm,
    onCancel,
    loading = false,
    ...props
}) => {
    // Variantes cromáticas outonais soft para o círculo do ícone
    const iconColors = {
        warning: {
            bg: 'rgba(226, 102, 28, 0.08)',
            text: 'var(--color-brand-accent)',
            border: 'rgba(226, 102, 28, 0.25)'
        },
        danger: {
            bg: 'rgba(239, 68, 68, 0.08)',
            text: '#ef4444',
            border: 'rgba(239, 68, 68, 0.25)'
        },
        success: {
            bg: 'rgba(16, 185, 129, 0.08)',
            text: '#10b981',
            border: 'rgba(16, 185, 129, 0.25)'
        },
        info: {
            bg: 'rgba(72, 39, 18, 0.08)',
            text: 'var(--color-brand-primary)',
            border: 'rgba(72, 39, 18, 0.2)'
        }
    };

    const activeColor = iconColors[iconVariant] || iconColors.warning;

    // Ações de rodapé utilizando NeoButtons outonais
    const footerActions = (
        <>
            <NeoButton 
                variant="outline-chocolate" 
                onClick={() => {
                    if (onCancel) onCancel();
                    toggle();
                }}
                disabled={loading}
                size="sm"
            >
                {cancelText}
            </NeoButton>
            <NeoButton 
                variant={iconVariant === 'danger' ? 'danger' : 'primary'}
                onClick={async () => {
                    toggle(); // Fecha o modal imediatamente (evita travar o backdrop do Reactstrap)
                    if (onConfirm) {
                        await onConfirm();
                    }
                }}
                loading={loading}
                size="sm"
                icon="mdi-check"
            >
                {confirmText}
            </NeoButton>
        </>
    );

    return (
        <NeoModal
            isOpen={isOpen}
            toggle={toggle}
            title={title}
            subtitle={subtitle}
            size="sm"
            centered={true}
            footerActions={footerActions}
            backdrop="static" // UX Ideal: impede fechar ao clicar fora no backdrop
            keyboard={false}  // UX Ideal: impede fechar ao pressionar a tecla ESC
            {...props}
        >

            <div className="text-center py-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {/* Ícone circular tátil e premium */}
                <div 
                    className="d-inline-flex align-items-center justify-content-center rounded-circle mb-3 animate__animated animate__pulse animate__infinite"
                    style={{ 
                        width: '68px', 
                        height: '68px', 
                        backgroundColor: activeColor.bg,
                        border: `2px solid ${activeColor.border}`,
                        color: activeColor.text,
                        fontSize: '32px'
                    }}
                >
                    <i className={`mdi ${icon}`}></i>
                </div>
                
                {/* Mensagem central */}
                <p 
                    className="font-size-14 text-muted fw-semibold mb-0 px-1"
                    style={{ 
                        color: 'var(--color-brand-secondary)', 
                        lineHeight: '1.6',
                        letterSpacing: '0.2px'
                    }}
                >
                    {message}
                </p>
            </div>
        </NeoModal>
    );
};

NeoConfirmDialog.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    toggle: PropTypes.func.isRequired,
    title: PropTypes.string,
    subtitle: PropTypes.string,
    message: PropTypes.string,
    icon: PropTypes.string,
    iconVariant: PropTypes.oneOf(['warning', 'danger', 'success', 'info']),
    confirmText: PropTypes.string,
    cancelText: PropTypes.string,
    onConfirm: PropTypes.func.isRequired,
    onCancel: PropTypes.func,
    loading: PropTypes.bool
};

export default NeoConfirmDialog;
